import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Save, ArrowUp, ArrowDown, Trash2, Plus } from "lucide-react";

export default function NavTab() {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/nav");
      setItems(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const arr = [...items];
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    setItems(arr.map((it, i) => ({ ...it, order: i + 1 })));
  };

  const setField = (idx, key, value) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));
  };

  const remove = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx).map((it, i) => ({ ...it, order: i + 1 })));
  };

  const add = () => {
    setItems((prev) => [
      ...prev,
      { id: `new-${Date.now()}`, order: prev.length + 1, label: "Menu Baru", href: "/", enabled: true },
    ]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/nav", items.map((it, i) => ({
        id: it.id, order: i + 1, label: it.label, href: it.href, enabled: it.enabled !== false,
      })));
      toast.success("Menu navigasi tersimpan");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="nav-tab">
      <p className="text-sm text-stone-600">
        Atur label &amp; urutan menu di header. Perubahan berlaku setelah <em>Simpan</em>.
      </p>
      <div className="bg-white border border-stone-200 divide-y divide-stone-100">
        {items.map((it, idx) => (
          <div key={it.id || idx} className="flex gap-2 items-center p-3" data-testid={`nav-row-${idx}`}>
            <div className="flex flex-col gap-1">
              <button onClick={() => move(idx, -1)} className="p-1 text-stone-500 hover:text-[#F39237]" data-testid={`nav-up-${idx}`}>
                <ArrowUp size={14} />
              </button>
              <button onClick={() => move(idx, 1)} className="p-1 text-stone-500 hover:text-[#F39237]" data-testid={`nav-down-${idx}`}>
                <ArrowDown size={14} />
              </button>
            </div>
            <input
              type="text"
              value={it.label}
              onChange={(e) => setField(idx, "label", e.target.value)}
              placeholder="Label"
              data-testid={`nav-label-${idx}`}
              className="flex-1 min-w-0 h-10 px-3 border border-stone-300 focus:border-[#F39237] outline-none"
            />
            <input
              type="text"
              value={it.href}
              onChange={(e) => setField(idx, "href", e.target.value)}
              placeholder="/#hash atau /path"
              data-testid={`nav-href-${idx}`}
              className="flex-1 min-w-0 h-10 px-3 border border-stone-300 focus:border-[#F39237] outline-none"
            />
            <label className="text-xs flex items-center gap-1 whitespace-nowrap">
              <input
                type="checkbox"
                checked={it.enabled !== false}
                onChange={(e) => setField(idx, "enabled", e.target.checked)}
              />
              Aktif
            </label>
            <button
              onClick={() => remove(idx)}
              className="p-2 text-stone-500 hover:text-red-600"
              data-testid={`nav-delete-${idx}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <button
          onClick={add}
          data-testid="nav-add"
          className="inline-flex items-center gap-2 px-4 py-2.5 border border-stone-300 hover:border-[#F39237] text-sm"
        >
          <Plus size={14} /> Tambah Menu
        </button>
        <button
          onClick={save}
          disabled={saving}
          data-testid="nav-save"
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#0A0A0A] text-white hover:bg-[#F39237] hover:text-[#0A0A0A] text-sm disabled:opacity-60"
        >
          <Save size={14} /> {saving ? "Menyimpan…" : "Simpan"}
        </button>
      </div>
    </div>
  );
}
